import { FilteredCreditApplication, useFeatureFlags } from '@applyonline/shared'
import { EApplicantType, EDocumentStatus, EDocumentType, EExternalStep, EFinancingProgram } from '@if/core/types'
import { getEnvRuntimeVariable } from '@if/core/utils'
import { useTranslation } from 'react-i18next'
import { applicantFullName } from '../../../../../data/credit-application-selectors'
import CreditStep, { CreditStepStatus } from './CreditStep'

interface IdentityVerificationCreditStepProps {
  creditApp: FilteredCreditApplication
  applicantType: EApplicantType
  noConnector?: boolean
}


export const isIdentityVerificationStepVisible = (
  creditApp: FilteredCreditApplication,
  applicantType: EApplicantType,
  enableVouchedForThinCreditB2B: boolean | undefined,
): boolean => {
  // Feature switch: the whole Personal thin-file Vouched flow is off unless this is on.
  if (!enableVouchedForThinCreditB2B) return false
  // Local dev: skip Vouched the same way B2C does
  if (!getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_VOUCHED)) return false
  // Thin-file trigger (B2B-only): the `identityVerification` RequiredExternalStep is the signal
  // that this applicant must complete Vouched.
  return creditApp.requiredExternalSteps.some(
    (x) => x.applicantType === applicantType && x.externalStepId === EExternalStep.IdentityVerification,
  )
}

const IdentityVerificationCreditStep = ({
  creditApp,
  applicantType,
  noConnector,
}: IdentityVerificationCreditStepProps) => {
  const { t } = useTranslation()
  const [flags] = useFeatureFlags(EFinancingProgram.Personal)
  const applicant = applicantType === EApplicantType.Applicant ? creditApp.applicant : creditApp.coapplicant!

  if (!isIdentityVerificationStepVisible(creditApp, applicantType, flags?.enableVouchedForThinCreditB2B)) return null

  // Status is driven by the ValidPhotoId document Vouched produces — same document-centric
  // model as B2C: Approved unblocks payout; AwaitingApproval is pending agent review.
  const idDocument = creditApp.documents.find(
    (x) => x.typeId === EDocumentType.ValidPhotoId && x.applicantType === applicantType,
  )

  const status =
    idDocument?.status === EDocumentStatus.Approved
      ? CreditStepStatus.Completed
      : idDocument?.status === EDocumentStatus.AwaitingApproval
        ? CreditStepStatus.Await
        : CreditStepStatus.RequiresAttention

  const description =
    status === CreditStepStatus.Completed
      ? 'overview.vouchedConfirmed'
      : status === CreditStepStatus.Await
        ? 'overview.vouchedPending'
        : 'overview.vouchedVerify'

  return (
    <CreditStep
      status={status}
      noConnector={noConnector}
      linkToDetail={`${applicantType.toLowerCase()}-identity-verification`}
      hideLinkToDetail={status === CreditStepStatus.Completed}
      title={t('overview.vouchedStep', { name: applicantFullName(applicant) })}
      content={t(description)}
    />
  )
}

export default IdentityVerificationCreditStep
